/**
 * Authentication Context
 * 
 * Provides Firebase authentication state management across the application
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authService, type UserWithRole, type AppName } from '../services/authService';
import type { User } from '../models/types';

interface AuthState {
  user: UserWithRole | null;
  loading: boolean;
  error: string | null;
  /**
   * OS-INV-04: Firebase custom-claim tenant memberships (e.g. ['offshore']).
   * Read from getIdTokenResult().claims.tenants. Empty array when the user has
   * no tenant claim or is signed out. Used by OS routes to gate access; Made.bb
   * routes do not read this so their behavior is unchanged.
   */
  tenants: string[];
}

interface AuthContextType extends AuthState {
  signInWithGoogle: (app?: AppName) => Promise<UserWithRole>;
  signInWithEmail: (email: string, password: string, app?: AppName) => Promise<UserWithRole>;
  createAccount: (email: string, password: string, displayName: string) => Promise<UserWithRole>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  sendEmailVerification: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserWithRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [tenants, setTenants] = useState<string[]>([]);

  useEffect(() => {
    // Subscribe to authentication state changes
    const unsubscribe = authService.onAuthStateChange((newUser) => {
      setUser(newUser);
      setIsAdmin(authService.isAdminOrAbove());
      setLoading(false);
      setError(null);

      // OS-INV-04: resolve tenant custom claims from the Firebase ID token.
      // Non-blocking — Made.bb routes never read `tenants`, so a delayed/empty
      // result has zero impact on existing behavior.
      if (newUser) {
        authService
          .getTenantClaims()
          .then(setTenants)
          .catch(() => setTenants([]));
      } else {
        setTenants([]);
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    // Auto sign out when page is closed or refreshed
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Don't sign out on public routes
      const publicRoutes = [
        '/madebb/status/',
        '/madebb/',
        '/madebb/login',
        '/os/',
        '/os'
      ];
      const currentPath = window.location.pathname;
      const isPublicRoute = publicRoutes.some(route => currentPath.startsWith(route));

      if (user && !isPublicRoute) {
        // Sign out silently (don't wait for async)
        authService.signOut().catch(console.error);

        // Optional: Show confirmation dialog
        // event.preventDefault();
        // event.returnValue = 'Are you sure you want to leave? You will be signed out.';
        // return event.returnValue;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && user) {
        // Page is being hidden (tab switch, minimize, etc.)
        // Could implement auto-signout after a delay
        console.log('Page hidden - user session maintained');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  const signInWithGoogle = async (app?: AppName): Promise<User> => {
    setLoading(true);
    setError(null);
    try {
      const user = await authService.signInWithGoogle(app);
      return user;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signInWithEmail = async (email: string, password: string, app?: AppName): Promise<User> => {
    setLoading(true);
    setError(null);
    try {
      const user = await authService.signInWithEmail(email, password, app);
      return user;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createAccount = async (email: string, password: string, displayName: string): Promise<User> => {
    setLoading(true);
    setError(null);
    try {
      const user = await authService.createAccount(email, password, displayName);
      return user;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await authService.signOut();
      
      // Clear all local storage related to sessions
      localStorage.removeItem('admin-api-keys');
      localStorage.removeItem('user-session');
      localStorage.removeItem('sail-measurements');
      localStorage.removeItem('boat-info');
      
      console.log('🧹 Cleared local session data on sign out');
      
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const sendEmailVerification = async (): Promise<void> => {
    try {
      await authService.sendEmailVerification();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    tenants,
    signInWithGoogle,
    signInWithEmail,
    createAccount,
    signOut,
    isAdmin,
    sendEmailVerification
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};